import React, { useCallback, useEffect, useState } from "react";
import {
  Clock,
  Pencil,
  Plus,
  Sparkles,
  X,
  ArrowRight,
  Timer,
} from "lucide-react";
import { GlobalButton, GlobalTextField } from "../../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../../global/GlobalTableActionButton";
import { GlobalModal } from "../../../../../global/GlobalModal";
import {
  fetchAttendanceShifts,
  createAttendanceShift,
  updateAttendanceShift,
  deleteAttendanceShift,
} from "../../../api/attendanceApi";

const DEFAULT_GRACE = 15;

function formatTime12(hm) {
  const raw = String(hm ?? "").trim();
  if (!/^\d{2}:\d{2}$/.test(raw)) return raw || "—";
  const [h, m] = raw.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${suffix}`;
}

function minutesFromHm(hm) {
  const raw = String(hm ?? "").trim();
  if (!/^\d{2}:\d{2}$/.test(raw)) return 0;
  const [h, m] = raw.split(":").map(Number);
  return h * 60 + m;
}

function shiftDurationLabel(start, end) {
  const startMin = minutesFromHm(start);
  let endMin = minutesFromHm(end);
  if (endMin <= startMin) endMin += 24 * 60;
  const total = endMin - startMin;
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

function isOvernight(start, end) {
  return minutesFromHm(end) <= minutesFromHm(start);
}

export default function AttendanceShiftsSection({ onNotify }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    id: null,
    shift_name: "",
    start_time: "",
    end_time: "",
    grace_minutes: DEFAULT_GRACE,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAttendanceShifts();
      setRows(data);
    } catch (err) {
      onNotify?.(err.message, { variant: "error" });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [onNotify]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const resetForm = () => {
    setForm({
      id: null,
      shift_name: "",
      start_time: "",
      end_time: "",
      grace_minutes: DEFAULT_GRACE,
    });
    setIsEditing(false);
    setFormError("");
  };

  const handleEdit = (row) => {
    setForm({
      id: row.id,
      shift_name: row.shift_name,
      start_time: row.start_time,
      end_time: row.end_time,
      grace_minutes: row.grace_minutes ?? DEFAULT_GRACE,
    });
    setIsEditing(true);
    setFormError("");
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setFormError("");

    const shift_name = String(form.shift_name ?? "").trim();
    const start_time = String(form.start_time ?? "").trim();
    const end_time = String(form.end_time ?? "").trim();
    const grace_minutes = Number(form.grace_minutes ?? DEFAULT_GRACE);

    if (!shift_name) {
      setFormError("Give the shift a name.");
      return;
    }
    if (!start_time || !end_time) {
      setFormError("Set both start and end times.");
      return;
    }
    if (start_time === end_time) {
      setFormError("Start and end time cannot be the same.");
      return;
    }
    if (!Number.isInteger(grace_minutes) || grace_minutes < 0 || grace_minutes > 120) {
      setFormError("Grace minutes must be between 0 and 120.");
      return;
    }

    const payload = { shift_name, start_time, end_time, grace_minutes };

    setSaving(true);
    try {
      if (isEditing && form.id) {
        await updateAttendanceShift(form.id, payload);
        onNotify?.("Shift updated.", { variant: "success" });
      } else {
        await createAttendanceShift(payload);
        onNotify?.("Shift saved.", { variant: "success" });
      }
      resetForm();
      loadRows();
    } catch (err) {
      setFormError(err.message || "Failed to save shift.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAttendanceShift(deleteTarget.id);
      onNotify?.("Shift removed.", { variant: "success" });
      if (form.id === deleteTarget.id) resetForm();
      setDeleteTarget(null);
      loadRows();
    } catch (err) {
      onNotify?.(err.message, { variant: "error" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section id="att-shifts" className="att-panel att-panel--shifts">
      <div className="att-panel__head att-panel__head--compact">
        <div className="att-panel__icon" aria-hidden>
          <Clock size={20} />
        </div>
        <div>
          <h3 className="att-panel__title">Shift timings</h3>
          <p className="att-panel__hint">
            Define work windows — staff check-in is expected within the shift plus grace period.
          </p>
        </div>
      </div>

      <div className="att-panel__body att-panel__body--shifts">
        <form
          className={`att-shift-composer${isEditing ? " att-shift-composer--editing" : ""}`}
          onSubmit={handleSubmit}
        >
          <div className="att-shift-composer__label">
            {isEditing ? (
              <>
                <Pencil size={14} aria-hidden />
                Editing shift
              </>
            ) : (
              <>
                <Plus size={14} aria-hidden />
                Add shift
              </>
            )}
          </div>

          <div className="att-shift-composer__fields">
            <GlobalTextField
              label="Shift name"
              value={form.shift_name}
              onChange={(v) => setForm((p) => ({ ...p, shift_name: v }))}
              placeholder="Morning, General…"
              required
            />
            <GlobalTextField
              label="Starts"
              type="time"
              value={form.start_time}
              onChange={(v) => setForm((p) => ({ ...p, start_time: v }))}
              required
            />
            <GlobalTextField
              label="Ends"
              type="time"
              value={form.end_time}
              onChange={(v) => setForm((p) => ({ ...p, end_time: v }))}
              required
            />
            <GlobalTextField
              label="Grace (min)"
              type="number"
              value={String(form.grace_minutes ?? DEFAULT_GRACE)}
              onChange={(v) => setForm((p) => ({ ...p, grace_minutes: Number(v) }))}
              min={0}
              max={120}
              required
            />
          </div>

          <div className="att-shift-composer__actions">
            {isEditing ? (
              <button
                type="button"
                className="att-hol-composer__cancel"
                onClick={resetForm}
                disabled={saving}
                aria-label="Cancel edit"
              >
                <X size={16} />
              </button>
            ) : null}
            <GlobalButton type="submit" variant="primary" disabled={saving}>
              {saving ? "Saving…" : isEditing ? "Update" : "Add"}
            </GlobalButton>
          </div>

          {formError ? (
            <p className="att-shift-composer__error" role="alert">
              {formError}
            </p>
          ) : null}
        </form>

        <div className="att-shift-list-wrap">
          {loading ? (
            <p className="att-empty">Loading shifts…</p>
          ) : rows.length === 0 ? (
            <div className="att-hol-empty">
              <div className="att-hol-empty__icon" aria-hidden>
                <Sparkles size={26} strokeWidth={1.5} />
              </div>
              <p>No shifts yet.</p>
              <span>Add your first work window above.</span>
            </div>
          ) : (
            <ul className="att-shift-list">
              {rows.map((row) => {
                const overnight = isOvernight(row.start_time, row.end_time);
                const isActive = form.id === row.id && isEditing;
                return (
                  <li
                    key={row.id}
                    className={`att-shift-card${isActive ? " att-shift-card--active" : ""}`}
                  >
                    <div className="att-shift-card__time att-shift-card__time--start">
                      <span>{formatTime12(row.start_time)}</span>
                      <small>Start</small>
                    </div>

                    <div className="att-shift-card__track" aria-hidden>
                      <span className="att-shift-card__track-line" />
                      <ArrowRight size={14} className="att-shift-card__track-arrow" />
                      {overnight ? (
                        <span className="att-shift-card__overnight">+1 day</span>
                      ) : null}
                      <span className="att-shift-card__duration">
                        {shiftDurationLabel(row.start_time, row.end_time)}
                      </span>
                    </div>

                    <div className="att-shift-card__time att-shift-card__time--end">
                      <span>{formatTime12(row.end_time)}</span>
                      <small>End</small>
                    </div>

                    <div className="att-shift-card__body">
                      <h5>{row.shift_name}</h5>
                      <span className="att-shift-card__grace">
                        <Timer size={12} aria-hidden />
                        {row.grace_minutes ?? 0} min grace
                      </span>
                    </div>

                    <div className="att-shift-card__actions">
                      <GlobalTableActionButton action="edit" onClick={() => handleEdit(row)} />
                      <GlobalTableActionButton action="delete" onClick={() => setDeleteTarget(row)} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <GlobalModal
        open={Boolean(deleteTarget)}
        title="Remove shift?"
        onClose={() => !deleting && setDeleteTarget(null)}
        footer={
          <>
            <GlobalButton
              type="button"
              variant="ghost"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Cancel
            </GlobalButton>
            <GlobalButton type="button" variant="danger" onClick={confirmDelete} disabled={deleting}>
              {deleting ? "Removing…" : "Remove"}
            </GlobalButton>
          </>
        }
      >
        <p>
          Remove <strong>{deleteTarget?.shift_name}</strong> (
          {formatTime12(deleteTarget?.start_time)} – {formatTime12(deleteTarget?.end_time)})?
        </p>
      </GlobalModal>
    </section>
  );
}
